import React from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, X, Loader2 } from 'lucide-react';

interface FileUploadProps {
  onUpload: (files: File[]) => Promise<void>;
  isUploading: boolean;
  uploadedFiles: { name: string; path: string; size: number }[];
  onRemove: (path: string) => void;
}

export function FileUpload({ onUpload, isUploading, uploadedFiles, onRemove }: FileUploadProps) {
  const [removing, setRemoving] = React.useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt']
    },
    maxSize: 10 * 1024 * 1024,
    multiple: true,
    disabled: isUploading,
    noClick: true,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length === 0 || isUploading) return;
      await onUpload(acceptedFiles);
    },
    onDropRejected: (rejectedFiles) => {
      const errors = rejectedFiles.map(rejection => {
        const error = rejection.errors[0];
        if (error.code === 'file-too-large') {
          return `${rejection.file.name} is too large. Maximum size is 10MB.`;
        }
        if (error.code === 'file-invalid-type') {
          return `${rejection.file.name} is not supported. Only PDF, DOCX, and TXT files are allowed.`;
        }
        return `${rejection.file.name} could not be uploaded: ${error.message}`;
      });
      alert(errors.join('\n'));
    }
  });

  const handleRemove = async (path: string) => {
    if (removing || isUploading) return;
    try {
      setRemoving(path);
      await onRemove(path);
    } catch (error) {
      console.error('Error removing file:', error);
      alert('Failed to remove file. Please try again.');
    } finally {
      setRemoving(null);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FileText className="h-5 w-5 text-red-500" />;
      case 'docx':
        return <FileText className="h-5 w-5 text-blue-500" />;
      case 'txt':
        return <FileText className="h-5 w-5 text-gray-500" />;
      default:
        return <FileText className="h-5 w-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-4">
      <div
        {...getRootProps()}
        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
          isDragActive ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300'
        } ${isUploading ? 'opacity-50 cursor-not-allowed' : 'hover:border-indigo-300 hover:bg-indigo-50'}`}
      >
        <div className="space-y-1 text-center">
          <Upload className={`mx-auto h-12 w-12 ${isDragActive ? 'text-indigo-500' : 'text-gray-400'}`} />
          <div className="flex text-sm text-gray-600">
            <button
              type="button"
              onClick={open}
              disabled={isUploading}
              className={`font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none ${
                isUploading ? 'cursor-not-allowed opacity-50' : ''
              }`}
            >
              Upload files
            </button>
            <input {...getInputProps()} />
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs text-gray-500">PDF, DOCX, or TXT files up to 10MB</p>
        </div>
      </div>

      {uploadedFiles.length > 0 && (
        <div className="mt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files</h4>
          <ul className="divide-y divide-gray-200 bg-white rounded-lg border border-gray-200">
            {uploadedFiles.map((file) => (
              <li key={file.path} className="p-3 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center min-w-0 flex-1">
                  <div className="flex-shrink-0">
                    {getFileIcon(file.name)}
                  </div>
                  <div className="ml-2 truncate">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {file.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(file.path)}
                  className={`ml-4 text-gray-400 hover:text-red-500 transition-colors ${
                    removing === file.path || isUploading ? 'cursor-not-allowed opacity-50' : ''
                  }`}
                  disabled={removing === file.path || isUploading}
                >
                  {removing === file.path ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <X className="h-5 w-5" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isUploading && (
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      )}
    </div>
  );
}